/**
 * The watermark stamped on the estimate pictures, and the download button
 * over them — both set in Admin → Settings → Estimate output (app settings
 * `watermark` and `downloadButton`).
 *
 * The watermark is composited into the files on the server: the layered
 * visualizer's composite (visualizer.php) and a designed room's photoreal
 * pictures as they land (render-jobs.php, never the 360), with the same
 * numbers here and there (public/api/watermark-helper.php). A partner
 * retailer's own watermark (retailers.watermark_url) replaces the default
 * on its pages and renders. The admin preview draws the same placement in
 * CSS over the sample picture.
 */

export type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export const CORNERS: { id: Corner; label: string }[] = [
  { id: 'top-left', label: 'Top left' },
  { id: 'top-right', label: 'Top right' },
  { id: 'bottom-left', label: 'Bottom left' },
  { id: 'bottom-right', label: 'Bottom right' },
];

/** The built-in Trusscore mark the layered visualizer has always used. */
export const DEFAULT_WATERMARK_URL = '/images/visualizer/watermark.png';

export interface WatermarkSettings {
  enabled: boolean;
  /** The picture (a PNG with transparency); '' is the built-in mark. */
  imageUrl: string;
  /** Its width as a share of the picture's width, percent. */
  sizePct: number;
  opacity: number;
  position: Corner;
  /** Its distance from the edges, px on a 1920-wide picture (scaled with the picture). */
  margin: number;
  /** On the layered (static) visualizer's pictures. */
  onStatic: boolean;
  /** On a designed room's photoreal pictures (never the 360). */
  onRenders: boolean;
}

export const DEFAULT_WATERMARK: WatermarkSettings = {
  enabled: true,
  imageUrl: '',
  sizePct: 10,
  opacity: 0.5,
  position: 'top-right',
  margin: 10,
  onStatic: true,
  onRenders: false,
};

export interface DownloadButtonSettings {
  enabled: boolean;
  position: Corner;
  /** The button's size, px. */
  size: number;
  color: string;
  opacity: number;
  onStatic: boolean;
  onRenders: boolean;
}

export const DEFAULT_DOWNLOAD_BUTTON: DownloadButtonSettings = {
  enabled: true,
  position: 'bottom-left',
  size: 36,
  color: '#ffffff',
  opacity: 0.8,
  onStatic: true,
  onRenders: true,
};

const HEX = /^#[0-9a-f]{6}$/i;
const num = (v: unknown, lo: number, hi: number, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
};
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const corner = (v: unknown, fallback: Corner): Corner => (CORNERS.some((c) => c.id === v) ? (v as Corner) : fallback);
/** A picture URL as the admin upload gives it (a site path) or an https URL; anything else is the default. */
const pictureUrl = (v: unknown): string => {
  if (typeof v !== 'string') return '';
  const s = v.trim();
  return s === '' || /^(\/[^\s]*|https:\/\/[^\s]+)$/.test(s) ? s : '';
};

export function normalizeWatermark(raw: unknown): WatermarkSettings {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const D = DEFAULT_WATERMARK;
  return {
    enabled: bool(r.enabled, D.enabled),
    imageUrl: pictureUrl(r.imageUrl),
    sizePct: Math.round(num(r.sizePct, 2, 50, D.sizePct) * 10) / 10,
    opacity: num(r.opacity, 0, 1, D.opacity),
    position: corner(r.position, D.position),
    margin: Math.round(num(r.margin, 0, 200, D.margin)),
    onStatic: bool(r.onStatic, D.onStatic),
    onRenders: bool(r.onRenders, D.onRenders),
  };
}

export function normalizeDownloadButton(raw: unknown): DownloadButtonSettings {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const D = DEFAULT_DOWNLOAD_BUTTON;
  return {
    enabled: bool(r.enabled, D.enabled),
    position: corner(r.position, D.position),
    size: Math.round(num(r.size, 20, 72, D.size)),
    color: typeof r.color === 'string' && HEX.test(r.color.trim()) ? r.color.trim().toLowerCase() : D.color,
    opacity: num(r.opacity, 0, 1, D.opacity),
    onStatic: bool(r.onStatic, D.onStatic),
    onRenders: bool(r.onRenders, D.onRenders),
  };
}

/** The watermark's place over a 16:9 picture, as CSS (the admin preview; the server composites the same geometry). */
export function watermarkCss(w: WatermarkSettings): React.CSSProperties {
  const mx = `${(w.margin / 1920) * 100}%`;
  const my = `${(w.margin / 1080) * 100}%`;
  return {
    position: 'absolute',
    width: `${w.sizePct}%`,
    opacity: w.opacity,
    ...(w.position.startsWith('top') ? { top: my } : { bottom: my }),
    ...(w.position.endsWith('left') ? { left: mx } : { right: mx }),
  };
}

/** A file name for a downloaded picture: the project, then the view, safe for any file system. */
export function downloadFileName(project: string, view: string, ext = 'jpg'): string {
  const clean = (s: string) => s.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const parts = [clean(project), clean(view)].filter(Boolean);
  return `${parts.length ? parts.join('-') : 'estimate'}.${ext}`;
}
